import { products as seedProducts } from './products';

export const PRODUCTS_API = 'http://87.238.211.194:8746/products';
export const FALLBACK_IMAGE = 'https://via.placeholder.com/800x800?text=No+Image';

const ensureArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);
const uniqueArray = (list = []) => Array.from(new Set(list.filter(Boolean)));

const SIZE_TOKENS = new Set([
  '3XS',
  'XXXS',
  'XXS',
  'XS',
  'S',
  'SM',
  'S/M',
  'M',
  'ML',
  'M/L',
  'L',
  'L/XL',
  'XL',
  'XXL',
  'XXXL',
  'XXXXL',
  '2XL',
  '3XL',
  '4XL',
  '5XL',
  '6XL',
  '7XL',
  '8XL',
  'OS',
  'OSFA',
  'OSFM',
  'ONE',
  'ONESIZE',
  'UNICA',
  'UNI',
]);

const normalizeSizeToken = (value) => (typeof value === 'string' ? value.trim().toUpperCase() : undefined);

const isSizeToken = (value) => {
  const token = normalizeSizeToken(value);
  if (!token) {
    return false;
  }
  if (SIZE_TOKENS.has(token)) {
    return true;
  }
  return /^\d{2}$/.test(token);
};

const extractBaseNameAndSize = (title = '') => {
  if (!title) {
    return { baseName: '', size: null };
  }

  let working = title.trim();
  let size = null;

  const parenMatch = working.match(/\(([^)]+)\)\s*$/);
  if (parenMatch) {
    const candidate = parenMatch[1].trim();
    if (isSizeToken(candidate)) {
      size = normalizeSizeToken(candidate);
      working = working.replace(/\(([^)]+)\)\s*$/, '').trim();
    }
  }

  if (!size) {
    const tokens = working.split(/\s+/);
    const lastToken = tokens[tokens.length - 1];
    if (isSizeToken(lastToken)) {
      size = normalizeSizeToken(lastToken);
      tokens.pop();
      working = tokens.join(' ').trim();
    }
  }

  return {
    baseName: working || title,
    size,
  };
};

const slugify = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || null;

const toNumber = (value, fallback = 0) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const normalizeProduct = (item, index = 0) => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const originalTitle = item.title || item.name || 'Unnamed product';
  const { baseName, size } = extractBaseNameAndSize(originalTitle);
  const displayName = baseName || originalTitle;

  const categories = ensureArray(item.categories).length > 0
    ? ensureArray(item.categories)
    : item.category
    ? [item.category]
    : ['Uncategorized'];

  const images = ensureArray(item.imageUrls).length > 0
    ? ensureArray(item.imageUrls)
    : item.image
    ? [item.image]
    : [FALLBACK_IMAGE];

  const variants = ensureArray(item.variants);
  const colorsFromVariants = variants
    .map((variant) => variant?.color)
    .filter(Boolean);
  const sizesFromVariants = variants
    .map((variant) => variant?.size)
    .filter(Boolean);

  const manualColors = ensureArray(item.colors);
  const manualSizes = ensureArray(item.sizes);
  if (size && !manualSizes.includes(size)) {
    manualSizes.push(size);
  }

  const specifications = ensureArray(item.specifications).map((spec, specIndex) => ({
    title: spec?.title || spec?.name || `Specification ${specIndex + 1}`,
    description: spec?.description || spec?.value || '',
  }));

  const brandName = item.brand || item.productBrand || 'Unknown brand';
  const colors = colorsFromVariants.length > 0 ? uniqueArray(colorsFromVariants) : uniqueArray(manualColors);
  const sizes = sizesFromVariants.length > 0 ? uniqueArray(sizesFromVariants) : uniqueArray(manualSizes);

  return {
    id: item.productCode || item.id?.toString() || `product-${index}`,
    productCode: item.productCode || item.id?.toString() || `product-${index}`,
    name: displayName,
    title: displayName,
    originalTitle,
    baseName: displayName,
    variantSize: size,
    brand: brandName,
    baseSlug: slugify(displayName),
    brandSlug: slugify(brandName),
    slug: slugify(`${brandName}-${displayName}`),
    price: toNumber(item.price, 0),
    oldPrice: toNumber(item.oldPrice, 0),
    currency: item.currency || '€',
    category: categories[0],
    categories,
    description: item.description || '',
    image: images[0],
    imageUrls: images,
    colors,
    sizes,
    details:
      specifications.length > 0
        ? specifications
        : ensureArray(item.details).map((detail, detailIndex) => ({
            title: detail?.title || `Detail ${detailIndex + 1}`,
            description: detail?.description || '',
          })),
    inStock:
      typeof item.storeStockQuantity === 'number'
        ? item.storeStockQuantity > 0
        : typeof item.storeSupplierQuantity === 'number'
        ? item.storeSupplierQuantity > 0
        : true,
    raw: item,
  };
};

const toLowerString = (value) =>
  typeof value === 'string'
    ? value.toLowerCase()
    : value !== undefined && value !== null
    ? value.toString().toLowerCase()
    : undefined;

const pickFirstKey = (...keys) => keys.find((key) => key !== undefined && key !== null);

const registerVariant = (baseProduct, variantLike, fallbackIndex = 0) => {
  if (!variantLike) {
    return;
  }

  const variantId =
    variantLike.id ||
    variantLike.productCode ||
    variantLike.raw?.productCode ||
    `${baseProduct.id}-variant-${fallbackIndex}`;

  const variantProductCode = variantLike.productCode || variantLike.raw?.productCode || variantId;
  if (baseProduct.variants.some((variant) => toLowerString(variant.productCode) === toLowerString(variantProductCode))) {
    return;
  }

  const colorCandidate = variantLike.color || variantLike.variantColor || variantLike.raw?.color;
  const sizeCandidate = variantLike.size || variantLike.variantSize || variantLike.raw?.size;
  const priceCandidate = typeof variantLike.price === 'number' ? variantLike.price : toNumber(variantLike.price, baseProduct.price || 0);

  baseProduct.variants.push({
    id: variantId,
    productCode: variantProductCode,
    name: variantLike.name || baseProduct.name,
    color: colorCandidate || null,
    size: sizeCandidate || null,
    price: priceCandidate,
    image: variantLike.image || baseProduct.image,
    raw: variantLike.raw || variantLike,
  });

  if (colorCandidate) {
    baseProduct.colors = Array.from(new Set([...(baseProduct.colors || []), colorCandidate]));
  }
  if (sizeCandidate) {
    baseProduct.sizes = Array.from(new Set([...(baseProduct.sizes || []), sizeCandidate]));
  }

  if (Number.isFinite(priceCandidate)) {
    baseProduct.minPrice = Math.min(baseProduct.minPrice, priceCandidate);
    baseProduct.maxPrice = Math.max(baseProduct.maxPrice, priceCandidate);
    baseProduct.price = baseProduct.minPrice;
  }
};

const normalizeAndGroupProducts = (list = []) => {
  const grouped = new Map();

  list.forEach((item, index) => {
    const normalized = normalizeProduct(item, index);
    if (!normalized) {
      return;
    }

    const baseKey = pickFirstKey(
      item.parentProductCode,
      item.parentProductId,
      item.productGroupCode,
      item.productCategoryCode,
      item.baseProductCode,
      normalized.raw?.parentProductCode,
      normalized.raw?.productGroupCode
    );

    const slugKey = normalized.slug || (normalized.brandSlug && normalized.baseSlug ? `${normalized.brandSlug}-${normalized.baseSlug}` : normalized.baseSlug);
    const finalKey = (baseKey ? baseKey.toString() : null) || slugKey || normalized.productCode || normalized.id;
    if (!grouped.has(finalKey)) {
      grouped.set(finalKey, {
        ...normalized,
        id: finalKey,
        productCode: finalKey,
        variants: [],
        colors: ensureArray(normalized.colors),
        sizes: ensureArray(normalized.sizes),
        minPrice: Number.isFinite(normalized.price) ? normalized.price : Number.POSITIVE_INFINITY,
        maxPrice: Number.isFinite(normalized.price) ? normalized.price : 0,
        imageUrls: ensureArray(normalized.imageUrls),
        categories: ensureArray(normalized.categories),
      });
    }

    const baseProduct = grouped.get(finalKey);
    baseProduct.currency = baseProduct.currency || normalized.currency || '€';

    registerVariant(baseProduct, {
      id: normalized.id,
      productCode: normalized.productCode,
      name: normalized.name,
      color: normalized.colors?.[0],
      size: normalized.variantSize || normalized.sizes?.[0],
      price: normalized.price,
      image: normalized.image,
      currency: normalized.currency,
      raw: normalized.raw,
    }, baseProduct.variants.length);

    ensureArray(normalized.variants).forEach((variant, variantIndex) => {
      registerVariant(baseProduct, variant, baseProduct.variants.length + variantIndex);
    });

    if (item.parentProductName) {
      baseProduct.name = item.parentProductName;
      baseProduct.title = item.parentProductName;
      baseProduct.baseName = item.parentProductName;
      baseProduct.slug = slugify(`${baseProduct.brand || 'brandless'}-${item.parentProductName}`) || baseProduct.slug;
    }

    baseProduct.imageUrls = uniqueArray([...(baseProduct.imageUrls || []), ...ensureArray(normalized.imageUrls)]);
    baseProduct.image = baseProduct.imageUrls[0] || baseProduct.image || normalized.image;
    baseProduct.categories = uniqueArray([...(baseProduct.categories || []), ...ensureArray(normalized.categories)]);
    baseProduct.inStock = baseProduct.inStock || normalized.inStock;
    if (!baseProduct.description && normalized.description) {
      baseProduct.description = normalized.description;
    }
  });

  return Array.from(grouped.values()).map((product) => ({
    ...product,
    minPrice:
      Number.isFinite(product.minPrice) && product.minPrice !== Number.POSITIVE_INFINITY
        ? product.minPrice
        : product.price,
    maxPrice: Number.isFinite(product.maxPrice) && product.maxPrice > 0 ? product.maxPrice : product.price,
    price:
      Number.isFinite(product.minPrice) && product.minPrice !== Number.POSITIVE_INFINITY
        ? product.minPrice
        : product.price,
  }));
};

export const normalizeProductList = (list = []) => normalizeAndGroupProducts(list).filter(Boolean);

export const getFallbackProducts = () => normalizeProductList(seedProducts);

export const fetchProducts = async () => {
  const response = await fetch(PRODUCTS_API);
  if (!response.ok) {
    throw new Error('Failed to fetch products');
  }

  const data = await response.json();
  if (!Array.isArray(data)) {
    throw new Error('Unexpected products payload');
  }

  return normalizeProductList(data);
};

const buildSearchUrl = (base, query) => {
  try {
    const url = new URL(base);
    if (query) {
      url.searchParams.set('search', query);
    }
    return url.toString();
  } catch (_error) {
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${query ? `${separator}search=${encodeURIComponent(query)}` : ''}`;
  }
};

export const searchProducts = async (query, options = {}) => {
  const normalizedQuery = typeof query === 'string' ? query.trim() : '';
  if (!normalizedQuery) {
    return [];
  }

  const { signal } = options;
  const searchUrl = buildSearchUrl(PRODUCTS_API, normalizedQuery);

  try {
    const response = await fetch(searchUrl, { signal });
    if (response.ok) {
      const payload = await response.json();
      if (Array.isArray(payload)) {
        const normalizedResults = normalizeProductList(payload);
        if (normalizedResults.length > 0) {
          return normalizedResults;
        }
      }
    }
  } catch (error) {
    if (error?.name === 'AbortError') {
      return [];
    }
  }

  const fallbackProducts = await fetchProducts();
  const lowered = normalizedQuery.toLowerCase();
  const matchesQuery = (value) =>
    typeof value === 'string' && value.toLowerCase().includes(lowered);

  return fallbackProducts.filter((product) => {
    if (matchesQuery(product.name) || matchesQuery(product.brand)) {
      return true;
    }

    if (ensureArray(product.categories).some((category) => matchesQuery(category))) {
      return true;
    }

    return ensureArray(product.variants).some(
      (variant) => matchesQuery(variant.name) || matchesQuery(variant.color)
    );
  });
};

export const findProductById = (products, productId) => {
  if (!productId) {
    return undefined;
  }

  const idAsString = productId.toString().toLowerCase();
  return products.find((product) => {
    const candidateId = product.id?.toString().toLowerCase();
    const candidateCode = product.productCode?.toString().toLowerCase();
    if (candidateId === idAsString || candidateCode === idAsString) {
      return true;
    }

    return ensureArray(product.variants).some((variant) => {
      const variantId = variant.id?.toString().toLowerCase();
      const variantCode = variant.productCode?.toString().toLowerCase();
      return variantId === idAsString || variantCode === idAsString;
    });
  });
};
